import { DatasetMetadataType } from '@routes/data-qa/datasets/dataset-metadata-type'
import {
  deleteHuggingFaceToken,
  saveHuggingFaceTokenIfNeeded,
  type SaveHuggingFaceTokenMutation,
} from '@routes/huggingface-tokens/huggingface-tokens-store'
import { BehaviorType, HuggingFaceDataset, type RunInfo } from '@routes/llm/runs/llm-run.svelte'

class SharedCustomDatasetTokenState {
  public useToken = $state(false)
  public token = $state<string | null | undefined>(null)
  public tokenId = $state<number | undefined>(undefined)
  public saveToken = $state(false)
  public tokenName = $state('')

  clear() {
    this.useToken = false
    this.token = null
    this.tokenId = undefined
    this.saveToken = false
    this.tokenName = ''
  }
}

const getCustomHuggingFaceDatasets = (runInfo: RunInfo) => {
  const huggingFaceDatasets: HuggingFaceDataset[] = []

  for (const behavior of runInfo.targetBehaviors) {
    if (behavior.type !== BehaviorType.CUSTOM) {
      continue
    }
    if (behavior.biasedDataset.type === DatasetMetadataType.HuggingFaceDataset) {
      huggingFaceDatasets.push(behavior.biasedDataset)
    }
    if (behavior.unbiasedDataset.type === DatasetMetadataType.HuggingFaceDataset) {
      huggingFaceDatasets.push(behavior.unbiasedDataset)
    }
  }

  for (const utility of runInfo.targetUtilities) {
    if (utility.dataset?.type === DatasetMetadataType.HuggingFaceDataset) {
      huggingFaceDatasets.push(utility.dataset)
    }
  }

  return huggingFaceDatasets
}

const cloneHuggingFaceDataset = (dataset: HuggingFaceDataset, huggingFaceDatasetName: string) =>
  new HuggingFaceDataset(huggingFaceDatasetName, {
    token: dataset.token,
    tokenId: dataset.tokenId,
    saveToken: dataset.saveToken,
    tokenName: dataset.tokenName,
  })

const applySharedTokenToCustomDatasets = (
  datasets: HuggingFaceDataset[],
  sharedTokenState: SharedCustomDatasetTokenState,
) => {
  for (const dataset of datasets) {
    dataset.token = sharedTokenState.token ?? null
    dataset.tokenId = sharedTokenState.tokenId
    dataset.saveToken = false
    dataset.tokenName = ''
  }
}

const cleanupCreatedDatasetTokens = async (
  savedDatasetTokens: { dataset: HuggingFaceDataset; createdTokenId: number }[],
) => {
  await Promise.allSettled(
    savedDatasetTokens.map(async ({ createdTokenId }) => {
      await deleteHuggingFaceToken(createdTokenId)
    }),
  )
}

const persistCustomDatasetTokens = async ({
  datasets,
  sharedTokenState,
  mutation,
}: {
  datasets: HuggingFaceDataset[]
  sharedTokenState: SharedCustomDatasetTokenState
  mutation: SaveHuggingFaceTokenMutation
}) => {
  if (sharedTokenState.useToken) {
    const createdTokenId = await saveHuggingFaceTokenIfNeeded({
      shouldSave: sharedTokenState.saveToken,
      token: sharedTokenState.token,
      tokenName: sharedTokenState.tokenName,
      mutation,
    })
    if (createdTokenId !== undefined) {
      sharedTokenState.tokenId = createdTokenId
      sharedTokenState.token = null
      sharedTokenState.saveToken = false
      sharedTokenState.tokenName = ''
    }
    applySharedTokenToCustomDatasets(datasets, sharedTokenState)
    return
  }

  const savedDatasetTokens: { dataset: HuggingFaceDataset; createdTokenId: number }[] = []

  try {
    for (const dataset of datasets) {
      const createdTokenId = await saveHuggingFaceTokenIfNeeded({
        shouldSave: dataset.saveToken,
        token: dataset.token,
        tokenName: dataset.tokenName,
        mutation,
      })
      if (createdTokenId === undefined) {
        continue
      }
      savedDatasetTokens.push({ dataset, createdTokenId })
    }
  } catch (error) {
    await cleanupCreatedDatasetTokens(savedDatasetTokens)
    throw error
  }

  for (const { dataset, createdTokenId } of savedDatasetTokens) {
    dataset.tokenId = createdTokenId
    dataset.token = null
    dataset.saveToken = false
    dataset.tokenName = ''
  }
}

export {
  applySharedTokenToCustomDatasets,
  cloneHuggingFaceDataset,
  getCustomHuggingFaceDatasets,
  persistCustomDatasetTokens,
  SharedCustomDatasetTokenState,
}
